/** @jsx jsx */
import { CDropdown, CDropdownItem, CDropdownMenu } from "@coreui/react"
import { css, jsx } from "@emotion/react"
import Feedback from "assets/icons/Feedback.svg"
import Notifications from "assets/icons/Notifications.svg"
import QuestionMark from "assets/icons/QuestionMark.svg"
import className from "classnames"
import { CMSButton } from "components/common/button"
import AccountProvider from "components/common/Context/AccountContext"
import DownloadPageProvider from "components/common/Context/DownloadPageContext"
import FeedbackDrawerProvider from "components/common/Context/FeedbackDrawerContext"
import MediaProvider from "components/common/Context/MediaContext"
import { useTheme } from "components/common/Context/ThemeContext"
import {
  AccountsSelectDropdown,
  ProjectSelectDropdown
} from "components/common/dropdown"
import ServiceRequestModal from "components/common/layout/ServiceRequestModal"
import { ServiceNowBanner } from "components/common/metrics"
import { ServiceActionModal } from "components/common/modal"
import { Tooltip } from "components/common/tooltip"
import { isExternalUser } from "constants/Constants"
import { navBarViews } from "extensions/utils/Classes"
import PropTypes from "prop-types"
import React, { useContext, useEffect, useRef, useState } from "react"
import ReactDOM from "react-dom"
import { useDispatch, useSelector } from "react-redux"
import { useHistory, useLocation } from "react-router-dom"
import { settingsActions } from "store"
import { useNavViews } from "utils"

import { ErrorBoundary } from "../error"
import { LoadingMessage, LoadingSpinnerCard } from "../loading"
import { WentWrongCard } from "../noData"
import CMSTypography, {
  fontSizes,
  fontWeights
} from "../typography/CMSTypography"
import {
  CreateServiceRequestButton,
  NotificationsDrawer,
  SurveyDrawer
} from "."

const useStyles = (theme) => {
  return {
    page: css`
      min-height: 80vh;
      padding-bottom: 10px;
      margin-bottom: 50px;
    `,
    pageHeader: css`
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin: 0 -15px;
      padding: 0.5rem 23px 0.5rem 1rem;
      position: sticky;
      top: 0;
      z-index: 110;
      background-color: ${theme.backgroundColor};
      box-shadow: 0px 3px 2px ${theme.pageHeaderBoxShadow};
      @media (max-width: 600px) {
        top: 60px;
        position: sticky;
        margin-bottom: 2rem !important;
      }
    `,
    mobileView: css`
      @media (max-width: 600px) {
        display: none !important;
      }
    `
  }
}

export default function Page({
  title = "",
  tooltipHeading,
  tooltipText,
  children
}) {
  const theme = useTheme()
  const classes = useStyles(theme)
  const { selectedNavBarView } = useNavViews()
  const [isAccountDropdownDisabled, setIsAccountDropdownDisabled] =
    useState(false)
  const [isSurveyDrawerOpen, setIsSurveyDrawerOpen] = useState(false)
  const [isNotificationsDrawerOpen, setIsNotificationsDrawerOpen] =
    useState(false)
  const { pathname } = useLocation()
  const pageHeadingClasses = className("titleStyles")
  const { isSmallScreen } = useContext(MediaProvider)
  const { projectBonding, projectBondLoading } = useContext(AccountProvider)
  const [
    isProjectAndAccountsDropdownVisible,
    setIsProjectAndAccountsDropdownVisible
  ] = useState(true)
  const [isAccountSelectDropdownOpen, setIsAccountSelectDropdownOpen] =
    useState(false)
  const [isProjectSelectDropdownOpen, setIsProjectSelectDropdownOpen] =
    useState(false)
  const {
    setIsTourOpen,
    setTourProfile,
    setCurrentTourStep,
    setIsRouteConfirmModalOpen
  } = useContext(DownloadPageProvider)

  const { setIsFeedbackDrawerOpen } = useContext(FeedbackDrawerProvider)
  const history = useHistory()

  const dispatch = useDispatch()
  const [notificationBadge, setNotificationBadge] = useState()

  const projectID = useSelector((state) => {
    return state.settings.currentProject
  })

  const projectNames = useSelector((state) => {
    return state.user.projectNames
  })

  const projectsFetching = useSelector((state) => {
    return state.user.projectsFetching
  })

  const accountsFetching = useSelector((state) => {
    return state.user.accountsFetching
  })

  const isAuthenticated = useSelector((state) => state.user.isAuthenticated)

  const [showServiceBanner, setShowServiceBanner] = useState(true)
  const [serviceRequestModal, setServiceRequestModal] = useState(false)
  const [show, setShow] = useState(false)
  const [showFeedbackMenu, setShowFeedbackMenu] = useState(false)
  const [menuShow, setMenuShow] = useState(false)

  const dropdownRef = useRef()
  const feedbackDropdownRef = useRef()
  useEffect(() => {
    if (pathname === "/infra-monitoring") setIsAccountDropdownDisabled(true)
    if (selectedNavBarView === navBarViews.internalView) {
      setIsProjectAndAccountsDropdownVisible(false)
    }
  }, [pathname, selectedNavBarView])
  // const feedbackJson =
  //   projectID === "PROJ0036094"
  //     ? [
  //         {
  //           label: "OpenCloud Portal Feedback",
  //           onClick: () => setIsFeedbackDrawerOpen(true)
  //         }
  //       ]
  //     : [
  //         // {
  //         //   label: "OpenCloud Portal Client Survey",
  //         //   onClick: () => setIsSurveyDrawerOpen(true)
  //         // },
  //         {
  //           label: "OpenCloud Portal Feedback",
  //           onClick: () => setIsFeedbackDrawerOpen(true)
  //         }
  //       ]
  const QuestionMarkJson = ["Tour Guide", "Service Request", "Training Videos"]

  const onCancel = () => {
    setServiceRequestModal(false)
  }
  const onContinue = () => {
    setServiceRequestModal(false)
    setShow(true)
  }

  function openTour() {
    if (pathname === "/") {
      setTourProfile(true)
      setCurrentTourStep(0)
      setIsTourOpen(true)
    } else {
      setIsRouteConfirmModalOpen(true)
    }
  }

  function openTraining() {
    history.push("/training-videos")
  }

  useEffect(() => {
    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setMenuShow(false)
      }
    }

    document.addEventListener("mousedown", handleClickOutside)
    return () => {
      document.removeEventListener("mousedown", handleClickOutside)
    }
  }, [dropdownRef, setMenuShow])
  useEffect(() => {
    function handleClickOutside(event) {
      if (
        feedbackDropdownRef.current &&
        !feedbackDropdownRef.current.contains(event.target)
      ) {
        setShowFeedbackMenu(false)
      }
    }

    document.addEventListener("mousedown", handleClickOutside)
    return () => {
      document.removeEventListener("mousedown", handleClickOutside)
    }
  }, [feedbackDropdownRef, setMenuShow])

  const userRole = useSelector((state) => state.user.role)
  useEffect(() => {
    const appContainer = document.getElementById("app-container")
    if ((isSurveyDrawerOpen || isNotificationsDrawerOpen) && appContainer) {
      appContainer.classList.add("overflow-hidden")
    }

    return () => {
      const appContainer = document.getElementById("app-container")
      if (appContainer) {
        appContainer.classList.remove("overflow-hidden")
      }
    }
  }, [isSurveyDrawerOpen, isNotificationsDrawerOpen])

  function projects() {
    return (
      projectNames?.map((proj) => ({
        text: proj.u_project_name,
        key: proj.u_number,
        u_project_pinned: proj.u_project_pinned
      })) || []
    )
  }
  const ProjectAccountDropDown = (
    <>
      <div className="d-flex" data-tut="project-account-selector">
        <ProjectSelectDropdown
          items={projects()}
          value={projectID}
          onChange={(projectId) => {
            dispatch(settingsActions.setProject(projectId))
          }}
          className=" project-dropdown project-select-dropdown-width mr-3"
          buttonClassName="padding-box AccountDropdown-Radius pageBoxShadow header-btn-style project-dropdown-text w-100"
          isLoading={projectsFetching}
          isProjectSelectDropdownOpen={isProjectSelectDropdownOpen}
          setIsProjectSelectDropdownOpen={setIsProjectSelectDropdownOpen}
        />

        <AccountsSelectDropdown
          isLoading={projectsFetching || accountsFetching}
          disabled={isAccountDropdownDisabled}
          isAccountSelectDropdownOpen={isAccountSelectDropdownOpen}
          setIsAccountSelectDropdownOpen={setIsAccountSelectDropdownOpen}
        />
      </div>
    </>
  )

  if (!isAuthenticated) return null
  return (
    <div css={classes.page}>
      <div css={classes.pageHeader} className={"mb-3 pg-header"}>
        {isSmallScreen ? (
          ""
        ) : (
          <div className="d-flex justify-content-center align-items-center">
            <div className={pageHeadingClasses}>
              <CMSTypography
                fontSize={fontSizes.larger}
                fontWeight={fontWeights.medium}
              >
                {title || ""}
              </CMSTypography>
            </div>
            {(tooltipHeading || tooltipText) && (
              <Tooltip
                heading={tooltipHeading}
                text={tooltipText}
                position="rightTop"
              />
            )}
          </div>
        )}
        {isSmallScreen ? (
          isProjectAndAccountsDropdownVisible && (
            <div
              className="d-flex w-100 mx-2 justify-content-between"
              data-tut="project-account-selector"
            >
              <ProjectSelectDropdown
                items={
                  projectNames?.map((proj) => ({
                    text: proj.u_project_name,
                    key: proj.u_number,
                    u_project_pinned: proj.u_project_pinned
                  })) || []
                }
                value={projectID}
                onChange={(projectId) => {
                  dispatch(settingsActions.setProject(projectId))
                }}
                className="project-dropdown mb-1 project-select-dropdown-width"
                buttonClassName="padding-box text-dark AccountDropdown-Radius pageBoxShadow header-btn-style project-dropdown-text w-100"
                isLoading={projectsFetching}
                isProjectSelectDropdownOpen={isProjectSelectDropdownOpen}
                setIsProjectSelectDropdownOpen={setIsProjectSelectDropdownOpen}
              />
              <AccountsSelectDropdown
                isLoading={projectsFetching || accountsFetching}
                mobileView={true}
                disabled={isAccountDropdownDisabled}
                isAccountSelectDropdownOpen={isAccountSelectDropdownOpen}
                setIsAccountSelectDropdownOpen={setIsAccountSelectDropdownOpen}
              />
            </div>
          )
        ) : (
          <div className="d-flex">
            {ReactDOM.createPortal(
              <SurveyDrawer
                isOpen={isSurveyDrawerOpen}
                setSidebarIsOpen={setIsSurveyDrawerOpen}
              />,
              document.getElementById("drawerHolder")
            )}
            {ReactDOM.createPortal(
              <ServiceRequestModal show={show} setShow={setShow} />,
              document.getElementById("serv-req-modal")
            )}
            {ReactDOM.createPortal(
              <ServiceActionModal
                isOpen={serviceRequestModal}
                onCancel={onCancel}
                onContinue={onContinue}
              />,
              document.getElementById("serv-action-modal")
            )}
            {ReactDOM.createPortal(
              <NotificationsDrawer
                isOpen={isNotificationsDrawerOpen}
                setSidebarIsOpen={setIsNotificationsDrawerOpen}
                setNotificationBadge={setNotificationBadge}
              />,
              document.getElementById("drawerHolder")
            )}
            {/* <DatadogBtn projectsFetching={projectsFetching} /> */}
            {isProjectAndAccountsDropdownVisible && ProjectAccountDropDown}
            <div className="ml-3" data-tut="survey-feedback-dropdown">
              <CDropdown innerRef={feedbackDropdownRef}>
                <div className="py-0 px-0 ml-3">
                  <CMSButton
                    type="secondary"
                    onClick={() => {
                      setShowFeedbackMenu((show) => !show)
                    }}
                    size="sm"
                  >
                    <img src={Feedback} alt="feedback" width="22" height="22" />
                  </CMSButton>
                </div>
                <CDropdownMenu
                  className={className("px-2", {
                    show: showFeedbackMenu
                  })}
                >
                  <CDropdownItem
                    href="https://surveyna.qualtrics.com/jfe/form/SV_87JMWAsHsyFqZhQ"
                    key={"CSAT Survey"}
                    className="dashedBorder dropdownItem"
                    target="_blank"
                  >
                    CSAT Survey
                  </CDropdownItem>

                  <CDropdownItem
                    onClick={() => setIsFeedbackDrawerOpen(true)}
                    key={"Portal Feedback"}
                    className="dashedBorder dropdownItem"
                  >
                    Portal Feedback
                  </CDropdownItem>
                </CDropdownMenu>
              </CDropdown>
            </div>
            <CDropdown innerRef={dropdownRef}>
              <div className="py-0 px-0 ml-3">
                <CMSButton
                  type="secondary"
                  onClick={() => setMenuShow((show) => !show)}
                  size="sm"
                >
                  <img
                    src={QuestionMark}
                    alt="feedback"
                    width="22"
                    height="22"
                  />
                </CMSButton>
              </div>
              <CDropdownMenu
                className={className("px-2", {
                  show: menuShow
                })}
              >
                <CDropdownItem
                  onClick={openTour}
                  key={QuestionMarkJson[0]}
                  className="dashedBorder dropdownItem"
                >
                  {QuestionMarkJson[0]}
                </CDropdownItem>
                {!projectBondLoading
                  ? (projectBonding?.request_e_bonded?.toLowerCase() ===
                      "true" ||
                      projectBonding?.change_e_bonded?.toLowerCase() ===
                        "true") && (
                      <CDropdownItem
                        onClick={() => setServiceRequestModal(true)}
                        key={QuestionMarkJson[1]}
                        className="dashedBorder dropdownItem"
                      >
                        {QuestionMarkJson[1]}
                      </CDropdownItem>
                    )
                  : ""}
                <CDropdownItem
                  onClick={openTraining}
                  key={QuestionMarkJson[2]}
                  className="dashedBorder dropdownItem"
                >
                  {QuestionMarkJson[2]}
                </CDropdownItem>
              </CDropdownMenu>
            </CDropdown>
            {!isExternalUser(userRole) && (
              <div className="ml-3" data-tut="notifications-bell-btn">
                <CMSButton
                  onClick={() => setIsNotificationsDrawerOpen(true)}
                  type="secondary"
                  size="sm"
                  className="position-relative"
                >
                  <img
                    src={Notifications}
                    alt="feedback"
                    width="22"
                    height="22"
                  />
                  {notificationBadge && (
                    <span className="notification-badge"></span>
                  )}
                </CMSButton>
              </div>
            )}
            <div className="ml-3">
              {!projectBondLoading
                ? projectBonding?.request_e_bonded?.toLowerCase() === "false" &&
                  projectBonding?.change_e_bonded?.toLowerCase() ===
                    "false" && <CreateServiceRequestButton />
                : ""}
            </div>
          </div>
        )}
      </div>
      {showServiceBanner && (
        <ErrorBoundary fallback={<WentWrongCard />}>
          <React.Suspense fallback={<LoadingSpinnerCard />}>
            <ServiceNowBanner setShowServiceBanner={setShowServiceBanner} />
          </React.Suspense>
        </ErrorBoundary>
      )}
      <React.Suspense fallback={<LoadingMessage blink />}>
        {projectsFetching || accountsFetching ? (
          <LoadingMessage blink />
        ) : (
          <>
            {!isSmallScreen ? (
              ""
            ) : (
              <p className="h4 titleStyles-mbl">{title || ""}</p>
            )}
            {children}
          </>
        )}
      </React.Suspense>
    </div>
  )
}

Page.propTypes = {
  title: PropTypes.string,
  children: PropTypes.node,
  tooltipHeading: PropTypes.string,
  tooltipText: PropTypes.oneOfType([PropTypes.string, PropTypes.node])
}
